import {ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { MemberStateService } from '../../services/member-state.service';
import {take, tap, catchError, first, mergeMap} from 'rxjs/operators';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormService } from '../../services/form.service';
import { MessageBoxService } from '../shared/message-box/message-box.service';
import { PaymentService } from '../../services/payment.service';
import { PaymentMoment } from '../../model/payment-moment';
import { LettersService } from '../../services/letters.service';
import { UnemploymentPaymentRequest } from '../../model/unemployment-payment-request';

import { UserContextService } from '../../services/user-context.service';
import {DynamicsQuestions} from '../../model/dynamics-questions';
import {SelectOptions} from '../../model/select-options';
import {SelectItem} from '../../model/select-item';
import {forkJoin, Observable, of} from 'rxjs';
import {Member} from '../../model/member';
import {QuestionFormData} from '../../model/question-form-data';
import {TicketResponse} from '../../model/ticket-response';
import { Select } from 'primeng/select';
import { AsyncPipe, DatePipe } from '@angular/common';
import { FileSizePipe } from '../../pipes/filesize.pipe';
import { ViewState, toViewState } from '../../model/view-state';

@Component({
    selector: 'myabvv-abvv-ask-question',
    standalone: true,
    templateUrl: './ask-question.component.html',
    styleUrls: ['./ask-question.component.css'],
    imports: [RouterLink, FormsModule, ReactiveFormsModule, Select, AsyncPipe, DatePipe, FileSizePipe, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AskQuestionComponent implements OnInit {
  private memberStateService = inject(MemberStateService);
  private formService = inject(FormService);
  private formBuilder = inject(FormBuilder);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private paymentService = inject(PaymentService);
  private lettersService = inject(LettersService);
  private messageBoxService = inject(MessageBoxService);
  private userContext = inject(UserContextService);

  private destroyRef = inject(DestroyRef);

  questionForm: FormGroup;
  member = signal<Member>(undefined!);
  isLoading = signal(false);
  hasError = signal(false);
  paymentDataState$: Observable<ViewState<{
    paymentMoment: PaymentMoment | null;
    unemploymentPaymentRequest: UnemploymentPaymentRequest | null;
  }>>;
  minFilesize = 0;
  maxFilesize = 1024 * 1024 * 3; // 3MB
  fileTypeValidationPattern = '^(image/.*|application/pdf)$';
  selectData = signal<SelectItem[]>([]);
  selectOptions = signal<SelectOptions>({
    multiple: false,
    theme: 'bootstrap4',
    closeOnSelect: true,
    allowClear: false,
    debug: false
  });
  dynamicsQuestions = signal<DynamicsQuestions[]>(undefined);
  missingDataMessage = signal<string | null>(null);
  missingDataHasLink = signal(false);
  selectedFileName = signal('');

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  constructor() {

    this.router.events.pipe(take(1)).subscribe({
      next: (e) => {
        if (e instanceof NavigationEnd) {
          this.initForm();
        }
      },
      error: err => console.error('[AskQuestionComponent] Error on router event:', err)
    });

    this.memberStateService.getMember().pipe(take(1),
      tap({
        next: (value: Member) => {
          this.member.set(value);
        },
        error: () => {
          this.router.navigate(['/unemployment']).then().catch();
        },
      })).subscribe();

    this.questionForm = this.formBuilder.group({
      email: [{value: '', disabled: false}, [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')]],
      content: [{value: '', disabled: false}, [Validators.required, Validators.minLength(1)]],
      permission: [{value: false, disabled: false}, [Validators.requiredTrue]],
      subject: [{value: '', disabled: false}, [Validators.required, Validators.pattern('^[^0].*')]],
      file: [{value: '', disabled: false}, []],
      filename: [{value: '', disabled: false}, []],
      filesize: [{value: 0, disabled: false}, [Validators.min(this.minFilesize), Validators.max(this.maxFilesize)]],
      filetype: [{value: '', disabled: false}, [Validators.pattern(this.fileTypeValidationPattern)]],
      filecontent: [{value: '', disabled: false}, []],
    });
  }

  ngOnInit() {
    this.initForm();
    this.setSelectPlaceholder();
    this.buildDynamicQuestionsArray();
    this.getLastPaymentAndRequestDates();

    this.translateService.onLangChange
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap({
          next: () => {
            this.setSelectPlaceholder();
            this.buildDynamicQuestionsArray();
          },
          error: err => console.error('[AskQuestionComponent] Error on language change:', err)
        }))
      .subscribe();

    this.questionForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: status => {
          if (status === 'VALID') {
            this.missingDataMessage.set(null);
          }
        },
        error: err => console.error('[AskQuestionComponent] Error on form status change:', err)
      });
  }

  setSelectPlaceholder() {
    this.translateService.get('ASK_QUESTION.chose_your_subject')
      .pipe(
        first(),
        tap({
          next: value => {
            this.selectOptions.update(prev => ({...prev, placeholder: value}));
          },
          error: err => console.error('[AskQuestionComponent] Error getting select placeholder translation:', err)
        }))
      .subscribe();
}

  buildDynamicQuestionsArray() {
    const dynamicQuestions$ = this.userContext.isInternalUser()
      ? this.formService.getDynamicQuestions(this.member().personalInformation.inss)
      : this.formService.getMyDynamicQuestions();

    dynamicQuestions$
      .pipe(
        first(),
        tap({
          next: (value: DynamicsQuestions[]) => {
            this.dynamicsQuestions.set(value);

            this.selectData.set(
              this.dynamicsQuestions().map((question: DynamicsQuestions) => ({
                  value: question.reference,
                  label: (this.translateService.currentLang || 'nl') === 'nl' ? question.dutchLabel : question.frenchLabel,
                  disabled: false
                })).filter(Boolean) as SelectItem[]
            );
          },
          error: err => console.error('[AskQuestionComponent] Error loading dynamic questions:', err)
        }))
      .subscribe();
  }

  initForm() {

    if (this.member().personalInformation.emails && this.member().personalInformation.emails[0]) {
      this.questionForm.controls['email'].setValue(this.member().personalInformation.emails[0].emailAddress.trim());
    }

    this.questionForm.controls['subject'].setValue('');
    this.questionForm.controls['content'].setValue('');
    this.questionForm.controls['permission'].setValue(false);
    this.onResetFile();
  }

  onNativeFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const reader = new FileReader();

    if (reader) {
      if (input.files && input.files.length) {
        const file = input.files[0];

        // Check file size
        if (file.size > this.maxFilesize) {
          this.messageBoxService.showModal('ASK_QUESTION.dialog_title',
            'ASK_QUESTION.file_too_large',
            'ASK_QUESTION.dialog_ok',
            '');
          this.onResetFile();
          return;
        }

        this.selectedFileName.set(file.name);
        this.questionForm.patchValue({filesize: file.size});
        this.questionForm.patchValue({filename: file.name});
        this.questionForm.patchValue({filetype: file.type});

        // When the filetype is unknown, Angular sets an empty string which makes the field valid
        // Let's put a value to make it false manually
        if (file.type === '') { this.questionForm.patchValue({filetype: 'false'}); }

        const regexpFileType = new RegExp(this.fileTypeValidationPattern);
        if (regexpFileType.test(file.type)) {
            reader.readAsDataURL(file);
            reader.onload = () => {
              this.questionForm.patchValue({filecontent: reader.result});
            };
        }
      }
    } else {
      this.messageBoxService.showModal('ASK_QUESTION.dialog_title',
        'ASK_QUESTION.browser_too_old',
        'ASK_QUESTION.dialog_ok',
        '');
    }
  }

  onResetFile() {
    this.questionForm.patchValue({file: ''});
    this.questionForm.patchValue({filename: ''});
    this.questionForm.patchValue({filesize: ''});
    this.questionForm.patchValue({filetype: ''});
    this.questionForm.patchValue({filecontent: ''});
    this.selectedFileName.set('');

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onSubmitQuestion() {
    if (this.userContext.isInternalUser()) {
      return;
    }

    const formData = new QuestionFormData(
      this.questionForm.controls['content'].value,
      this.questionForm.controls['subject'].value,
      this.questionForm.controls['permission'].value,
      this.questionForm.controls['filename'].value || '',
      this.questionForm.controls['filecontent'].value || '',
      this.questionForm.controls['subject'].value
    );

    this.isLoading.set(true);

    // External users use the /me endpoint.
    // The new Consumer answers with HTTP 200 + a TicketResponse body; the old Consumer
    // still answers with HTTP 204 (empty body, surfaced here as null). Both are handled.
    this.formService.postMyFormRequest(formData)
        .pipe(first())
        .subscribe({
          next: (response: TicketResponse | null) => this.handleSubmitResponse(response),
          error: () => this.handleSubmitError()
        });
  }

  private handleSubmitResponse(response: TicketResponse | null) {
    // A functional failure can occur even on HTTP 200: Dolibarr returned an error code.
    // We only flag a failure when 'error' is explicitly a non-zero number, so an unexpected
    // payload (e.g. a future DTO change dropping the field) degrades to a generic success
    // rather than a false error.
    if (response && typeof response.error === 'number' && response.error !== 0) {
      this.handleSubmitError();
      return;
    }

    this.hasError.set(false);
    this.isLoading.set(false);
    this.questionForm.reset();
    this.initForm();

    // Show the ticket reference when the Consumer provided one (new behaviour). The old 204
    // response and the "region not served" edge case (200 with ticketCreated=false,
    // ticketReference=null — the form is still forwarded via the legacy flow) fall back to
    // the generic success message.
    const ticketReference = response?.ticketReference;
    if (ticketReference) {
      this.messageBoxService.showModal('ASK_QUESTION.dialog_title',
        'ASK_QUESTION.send_success_with_reference',
        'ASK_QUESTION.dialog_ok',
        '',
        '/ask-question',
        { reference: ticketReference });
    } else {
      this.messageBoxService.showModal('ASK_QUESTION.dialog_title',
        'ASK_QUESTION.send_success',
        'ASK_QUESTION.dialog_ok',
        '',
        '/ask-question');
    }
  }

  private handleSubmitError() {
    this.hasError.set(true);
    this.isLoading.set(false);
    this.questionForm.reset();
    this.initForm();
    this.messageBoxService.showModal('ASK_QUESTION.dialog_title',
      'USER_FEEDBACK.error_occurred',
      'ASK_QUESTION.dialog_ok',
      '');
  }

  getFormValue(property: string): string {
    return this.questionForm.controls[property]?.value || null;
  }

  checkFormAndSubmit(): void {
    if (!this.questionForm.valid) {
      this.setMissingDataMessage();
    } else {
      this.missingDataMessage.set(null);
      this.onSubmitQuestion();
    }
  }

  isInternal() {
    return this.userContext.isInternalUser();
  }

  private setMissingDataMessage(): void {
    const key = this.getMissingDataMessageKey();
    if (!key) {
      this.missingDataMessage.set(null);
      this.missingDataHasLink.set(false);
      return;
    }

    this.missingDataHasLink.set(key === 'ASK_QUESTION.email_missing');

    this.translateService.get(key).pipe(take(1)).subscribe({
      next: translated => {
        this.missingDataMessage.set(translated);
      },
      error: err => console.error('[AskQuestionComponent] Error translating missing data message:', err)
    });
  }

  private getMissingDataMessageKey(): string | null {
    if (!this.questionForm.get('email')?.valid) {
      return 'ASK_QUESTION.email_missing';
    } else if (!this.questionForm.get('subject')?.valid) {
      return 'ASK_QUESTION.subject_missing';
    } else if (!this.questionForm.get('content')?.valid) {
      return 'ASK_QUESTION.content_missing';
    } else if (!this.questionForm.get('permission')?.valid) {
      return 'ASK_QUESTION.permission_missing';
    }
    return null;
  }

  private getLastPaymentAndRequestDates() {
    const data$ = this.userContext.isInternalUser()
      ? this.memberStateService.getMember().pipe(
          take(1),
          mergeMap((member: Member) => {
            const inss = member.personalInformation.inss;
            return forkJoin({
              paymentMoment: this.paymentService.getLastPayment(inss).pipe(catchError(() => of(null))),
              unemploymentPaymentRequest: this.lettersService.getLastPaymentRequest(inss).pipe(catchError(() => of(null)))
            });
          })
        )
      : forkJoin({
          paymentMoment: this.paymentService.getMyLastPayment().pipe(catchError(() => of(null))),
          unemploymentPaymentRequest: this.lettersService.getMyLastPaymentRequest().pipe(catchError(() => of(null)))
        });

    this.paymentDataState$ = data$.pipe(toViewState());
  }

}
